<template>
  <div v-if="isVisible" class="modal-backdrop" @click="$emit('close')">
    <div class="upload-draw-modal-redesign" @click.stop>
      <div class="modal-header">
        <h3>Upload Monthly Draw Documents</h3>
        <button class="close-button" @click="$emit('close')">×</button>
      </div>
      
      <div class="modal-body">
        <!-- Draw Number Selection (Always at top) -->
        <div class="draw-number-section">
          <label class="form-label">Draw Number</label>
          <div class="draw-input-wrapper">
            <select 
              v-model="selectedDrawNumber" 
              class="form-select draw-number-input"
              :disabled="useCustomDrawNumber"
            >
              <option value="" disabled>Select existing draw or create new</option>
              <!-- Existing draws -->
              <optgroup v-if="existingDraws.length > 0" label="📋 Existing Draws">
                <option v-for="draw in existingDraws" :key="draw.id" :value="draw.draw_number">
                  Draw {{ draw.draw_number }} {{ draw.period_through ? `(${formatDate(draw.period_through)})` : '' }}
                </option>
              </optgroup>
              <!-- New draw option -->
              <optgroup label="➕ Create New Draw">
                <option :value="suggestedDrawNumber">
                  Draw {{ suggestedDrawNumber }} (New)
                </option>
              </optgroup>
            </select>
            
            <!-- Custom Draw Number Toggle -->
            <div class="custom-draw-toggle">
              <label class="checkbox-label">
                <input 
                  type="checkbox" 
                  v-model="useCustomDrawNumber"
                  @change="toggleCustomDrawNumber"
                />
                <span class="checkbox-text">Enter custom draw number</span>
              </label>
            </div>
            
            <!-- Custom Draw Number Input -->
            <input 
              v-if="useCustomDrawNumber"
              type="text"
              v-model="selectedDrawNumber"
              class="form-control custom-draw-input"
              placeholder="Enter draw number (e.g., 22, APP-5)"
            />
            
            <small v-if="selectedDrawNumber && existingDraws.some(d => d.draw_number == selectedDrawNumber)" class="draw-hint existing-draw">
              ✅ Adding documents to existing Draw {{ selectedDrawNumber }}
            </small>
            <small v-else-if="selectedDrawNumber" class="draw-hint new-draw">
              ➕ Creating new Draw {{ selectedDrawNumber }}
            </small>
          </div>
        </div>

        <!-- Append Mode Checkbox (for Draw Tracker) -->
        <div v-if="buckets.draw_tracker.file" class="append-mode-section">
          <label class="checkbox-label append-checkbox">
            <input 
              type="checkbox" 
              v-model="appendMode"
            />
            <span class="checkbox-text">
              <strong>Append data</strong> instead of overwriting 
              <small class="append-hint">(Use when draw data is split across multiple files)</small>
            </span>
          </label>
        </div>

        <!-- 3 Bucket Upload Area -->
        <div class="upload-buckets-container">
          <!-- Bucket 1: Draw Tracker -->
          <div class="upload-bucket">
            <div class="bucket-header">
              <h4>📊 Draw Tracker</h4>
              <p class="bucket-description">Excel or PDF budget tracking</p>
            </div>
            <div 
              class="bucket-drop-zone"
              :class="{ 
                'drag-over': dragOverBucket === 'draw_tracker',
                'has-file': buckets.draw_tracker.file
              }"
              @dragover.prevent
              @dragenter.prevent="dragOverBucket = 'draw_tracker'"
              @dragleave.prevent="dragOverBucket = null"
              @drop.prevent="handleBucketDrop($event, 'draw_tracker')"
              @click="triggerFileInput('draw_tracker')"
            >
              <div v-if="!buckets.draw_tracker.file" class="drop-zone-empty">
                <span class="drop-icon">📁</span>
                <p class="drop-text">Drop file here or click to browse</p>
                <small class="file-types">.xls, .xlsx, .xlsm, .pdf</small>
              </div>
              <div v-else class="drop-zone-filled">
                <div class="file-info">
                  <span class="file-icon">📄</span>
                  <div class="file-details">
                    <span class="file-name">{{ buckets.draw_tracker.file.name }}</span>
                    <span class="file-type-badge">{{ buckets.draw_tracker.detectedType }}</span>
                  </div>
                  <button class="remove-file-btn" @click.stop="removeBucketFile('draw_tracker')">×</button>
                </div>
              </div>
            </div>
            <input 
              ref="drawTrackerInput"
              type="file" 
              accept=".xls,.xlsx,.xlsm,.pdf" 
              style="display: none"
              @change="handleFileInputChange($event, 'draw_tracker')"
            />
          </div>

          <!-- Bucket 2: Hard Costs (Payment Applications) -->
          <div class="upload-bucket">
            <div class="bucket-header">
              <h4>💼 Hard Costs</h4>
              <p class="bucket-description">G702/G703 Payment Apps</p>
            </div>
            <div 
              class="bucket-drop-zone"
              :class="{ 
                'drag-over': dragOverBucket === 'hard_costs',
                'has-file': buckets.hard_costs.files.length > 0
              }"
              @dragover.prevent
              @dragenter.prevent="dragOverBucket = 'hard_costs'"
              @dragleave.prevent="dragOverBucket = null"
              @drop.prevent="handleBucketDrop($event, 'hard_costs')"
              @click="triggerFileInput('hard_costs')"
            >
              <div v-if="buckets.hard_costs.files.length === 0" class="drop-zone-empty">
                <span class="drop-icon">📁</span>
                <p class="drop-text">Drop PDF(s) here or click to browse</p>
                <small class="file-types">.pdf only (multiple files supported)</small>
              </div>
              <div v-else class="drop-zone-filled">
                <!-- 🆕 MULTIPLE PDF SUPPORT: Show all files -->
                <div v-for="(file, index) in buckets.hard_costs.files" :key="index" class="file-info">
                  <span class="file-icon">📄</span>
                  <div class="file-details">
                    <span class="file-name">{{ file.name }}</span>
                    <span class="file-type-badge">PDF {{ index + 1 }} of {{ buckets.hard_costs.files.length }}</span>
                  </div>
                  <button class="remove-file-btn" @click.stop="removeBucketFile('hard_costs', index)">×</button>
                </div>
              </div>
            </div>
            <input 
              ref="hardCostsInput"
              type="file" 
              accept=".pdf" 
              multiple
              style="display: none"
              @change="handleFileInputChange($event, 'hard_costs')"
            />
          </div>

          <!-- Bucket 3: Invoices (Auto-tagged, bypasses page selection) -->
          <div class="upload-bucket">
            <div class="bucket-header">
              <h4>📋 Invoices</h4>
              <p class="bucket-description">Soft cost invoices (auto-tagged, skips page selection)</p>
            </div>
            <div 
              class="bucket-drop-zone"
              :class="{ 
                'drag-over': dragOverBucket === 'invoices',
                'has-file': buckets.invoices.files.length > 0
              }"
              @dragover.prevent
              @dragenter.prevent="dragOverBucket = 'invoices'"
              @dragleave.prevent="dragOverBucket = null"
              @drop.prevent="handleBucketDrop($event, 'invoices')"
              @click="triggerFileInput('invoices')"
            >
              <div v-if="buckets.invoices.files.length === 0" class="drop-zone-empty">
                <span class="drop-icon">📄</span>
                <p class="drop-text">Drop PDF(s) here or click to browse</p>
                <small class="file-types">.pdf only (multiple files supported)</small>
              </div>
              <div v-else class="drop-zone-filled">
                <!-- 🆕 INVOICE SUPPORT: Show all files -->
                <div v-for="(file, index) in buckets.invoices.files" :key="index" class="file-info">
                  <span class="file-icon">📄</span>
                  <div class="file-details">
                    <span class="file-name">{{ file.name }}</span>
                    <span class="file-type-badge">PDF {{ index + 1 }} of {{ buckets.invoices.files.length }}</span>
                  </div>
                  <button class="remove-file-btn" @click.stop="removeBucketFile('invoices', index)">×</button>
                </div>
              </div>
            </div>
            <input 
              ref="invoicesInput"
              type="file" 
              accept=".pdf"
              multiple
              style="display: none"
              @change="handleFileInputChange($event, 'invoices')"
            />
          </div>
        </div>

        <!-- Upload Summary -->
        <div v-if="hasAnyFiles" class="upload-summary">
          <h5>📦 Ready to Upload:</h5>
          <ul class="summary-list">
            <li v-if="buckets.draw_tracker.file">
              <strong>Draw Tracker:</strong> {{ buckets.draw_tracker.file.name }} 
              <span class="type-badge">{{ buckets.draw_tracker.detectedType }}</span>
            </li>
            <!-- 🆕 MULTIPLE PDF SUPPORT: Show all Hard Cost PDFs -->
            <li v-if="buckets.hard_costs.files.length > 0">
              <strong>Hard Costs:</strong> {{ buckets.hard_costs.files.length }} PDF{{ buckets.hard_costs.files.length > 1 ? 's' : '' }}
              <span class="type-badge">PDF Multi-Doc</span>
              <ul class="nested-file-list">
                <li v-for="(file, index) in buckets.hard_costs.files" :key="index">
                  {{ index + 1 }}. {{ file.name }}
                </li>
              </ul>
            </li>
            <!-- 🆕 INVOICE SUPPORT: Show all Invoice PDFs -->
            <li v-if="buckets.invoices.files.length > 0">
              <strong>Invoices:</strong> {{ buckets.invoices.files.length }} PDF{{ buckets.invoices.files.length > 1 ? 's' : '' }}
              <span class="type-badge">Invoice</span>
              <ul class="nested-file-list">
                <li v-for="(file, index) in buckets.invoices.files" :key="index">
                  {{ index + 1 }}. {{ file.name }}
                </li>
              </ul>
            </li>
          </ul>
          <p class="summary-note">
            <strong>Note:</strong> Hard Cost PDFs go through page selection. Invoice PDFs are auto-tagged and skip page selection.
          </p>
        </div>
      </div>
      
      <div class="modal-footer">
        <button class="btn-cancel" @click="$emit('close')">Cancel</button>
        <button 
          class="btn-upload" 
          :disabled="!canUpload || isUploading"
          @click="startUploadQueue"
        >
          <span v-if="isUploading" class="spinner-icon">⟳</span>
          {{ isUploading ? 'Uploading...' : `Upload Draw ${selectedDrawNumber || ''}` }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  isVisible: Boolean,
  projectId: [Number, String],
  existingDraws: Array,
  suggestedDrawNumber: [Number, String]
})

const emit = defineEmits(['close', 'uploadQueue'])

// Draw number management
const selectedDrawNumber = ref('')
const useCustomDrawNumber = ref(false)
const appendMode = ref(false)

// File buckets
const buckets = ref({
  draw_tracker: {
    file: null,
    detectedType: null // 'Excel Draw Tracker' or 'PDF Draw Tracker'
  },
  hard_costs: {
    files: [], // 🆕 MULTIPLE PDF SUPPORT: Array of files instead of single file
    detectedType: 'PDF Multi-Doc'
  },
  invoices: {
    files: [], // 🆕 INVOICE SUPPORT: Array of invoice PDFs (merged with HC)
    detectedType: 'Invoice'
  },
  soft_costs: {
    file: null,
    detectedType: null
  }
})

const dragOverBucket = ref(null)
const isUploading = ref(false)

// File input refs
const drawTrackerInput = ref(null)
const hardCostsInput = ref(null)
const invoicesInput = ref(null)

// Computed
const hasAnyFiles = computed(() => {
  return buckets.value.draw_tracker.file || buckets.value.hard_costs.files.length > 0 || buckets.value.invoices.files.length > 0
})

const canUpload = computed(() => {
  return hasAnyFiles.value && selectedDrawNumber.value && !isUploading.value
})

// Methods
const toggleCustomDrawNumber = () => {
  if (useCustomDrawNumber.value) {
    selectedDrawNumber.value = ''
  } else {
    selectedDrawNumber.value = props.suggestedDrawNumber
  }
}

const triggerFileInput = (bucket) => {
  if (bucket === 'draw_tracker') {
    drawTrackerInput.value?.click()
  } else if (bucket === 'hard_costs') {
    hardCostsInput.value?.click()
  } else if (bucket === 'invoices') {
    invoicesInput.value?.click()
  }
}

const handleFileInputChange = (event, bucket) => {
  if (bucket === 'hard_costs' || bucket === 'invoices') {
    // 🆕 MULTIPLE PDF SUPPORT: Handle multiple files for Hard Costs and Invoices
    const files = Array.from(event.target.files || [])
    files.forEach(file => processBucketFile(file, bucket))
  } else {
    // Single file for other buckets
    const file = event.target.files?.[0]
    if (file) {
      processBucketFile(file, bucket)
    }
  }
}

const handleBucketDrop = (event, bucket) => {
  dragOverBucket.value = null
  if (bucket === 'hard_costs' || bucket === 'invoices') {
    // 🆕 MULTIPLE PDF SUPPORT: Handle multiple dropped files
    const files = Array.from(event.dataTransfer.files || [])
    files.forEach(file => processBucketFile(file, bucket))
  } else {
    const file = event.dataTransfer.files?.[0]
    if (file) {
      processBucketFile(file, bucket)
    }
  }
}

const processBucketFile = (file, bucket) => {
  const extension = file.name.split('.').pop().toLowerCase()
  
  if (bucket === 'draw_tracker') {
    // Auto-detect Excel vs PDF
    if (['xls', 'xlsx', 'xlsm'].includes(extension)) {
      buckets.value.draw_tracker.file = file
      buckets.value.draw_tracker.detectedType = 'Excel Draw Tracker'
    } else if (extension === 'pdf') {
      buckets.value.draw_tracker.file = file
      buckets.value.draw_tracker.detectedType = 'PDF Draw Tracker'
    } else {
      alert('Invalid file type for Draw Tracker. Please use .xls, .xlsx, .xlsm, or .pdf')
    }
  } else if (bucket === 'hard_costs') {
    // 🆕 MULTIPLE PDF SUPPORT: Add to files array instead of replacing
    if (extension === 'pdf') {
      buckets.value.hard_costs.files.push(file)
    } else {
      alert('Invalid file type for Hard Costs. Please use .pdf only')
    }
  } else if (bucket === 'invoices') {
    // 🆕 INVOICE SUPPORT: Add to files array (merged with HC later)
    if (extension === 'pdf') {
      buckets.value.invoices.files.push(file)
    } else {
      alert('Invalid file type for Invoices. Please use .pdf only')
    }
  }
}

const removeBucketFile = (bucket, fileIndex = null) => {
  if (bucket === 'hard_costs' && fileIndex !== null) {
    // 🆕 MULTIPLE PDF SUPPORT: Remove specific file from array
    buckets.value.hard_costs.files.splice(fileIndex, 1)
  } else if (bucket === 'hard_costs') {
    // Remove all hard cost files
    buckets.value.hard_costs.files = []
  } else if (bucket === 'invoices' && fileIndex !== null) {
    // 🆕 INVOICE SUPPORT: Remove specific invoice file from array
    buckets.value.invoices.files.splice(fileIndex, 1)
  } else if (bucket === 'invoices') {
    // Remove all invoice files
    buckets.value.invoices.files = []
  } else {
    // Single file buckets
    buckets.value[bucket].file = null
    buckets.value[bucket].detectedType = null
  }
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const startUploadQueue = () => {
  if (!canUpload.value) return
  
  // Build queue of templates to process
  const queue = []
  
  if (buckets.value.draw_tracker.file) {
    queue.push({
      file: buckets.value.draw_tracker.file,
      templateType: buckets.value.draw_tracker.detectedType === 'Excel Draw Tracker' 
        ? 'excel_draw_tracker' 
        : 'pdf_draw_tracker',
      displayName: 'Draw Tracker',
      bucket: 'draw_tracker'
    })
  }
  
  // Process Hard Costs PDFs (normal PDF multi-doc processing with page selection)
  if (buckets.value.hard_costs.files.length > 0) {
    const hcCount = buckets.value.hard_costs.files.length
    
    queue.push({
      files: buckets.value.hard_costs.files,
      templateType: 'pdf_multi_doc',
      displayName: hcCount > 1 ? `Hard Costs (${hcCount} PDFs)` : `Hard Costs (${buckets.value.hard_costs.files[0].name})`,
      bucket: 'hard_costs',
      isMultiFile: hcCount > 1
    })
  }
  
  // 🎯 INVOICES: Use same pdf_multi_doc flow WITH page selection (just like Hard Costs)
  if (buckets.value.invoices.files.length > 0) {
    const invCount = buckets.value.invoices.files.length
    
    queue.push({
      files: buckets.value.invoices.files,
      templateType: 'pdf_multi_doc',  // Same as Hard Costs - goes to page selection
      displayName: invCount > 1 ? `Invoices (${invCount} PDFs)` : `Invoice (${buckets.value.invoices.files[0].name})`,
      bucket: 'invoices',
      isMultiFile: invCount > 1
      // 🚫 REMOVED: defaultDocType and autoAssignAllPages to enable manual page selection
      // User can now select which pages to process, just like Hard Costs
    })
  }
  
  // Emit event with queue data
  emit('uploadQueue', {
    drawNumber: selectedDrawNumber.value,
    appendMode: appendMode.value,
    queue: queue
  })
  
  isUploading.value = true
}

// Watch for modal closing to reset state
watch(() => props.isVisible, (newVal) => {
  if (!newVal) {
    // Reset state when modal closes
    isUploading.value = false
  }
})
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.upload-draw-modal-redesign {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
  margin: 0;
  font-size: 24px;
  color: #1f2937;
}

.close-button {
  background: none;
  border: none;
  font-size: 32px;
  color: #9ca3af;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  width: 32px;
  height: 32px;
}

.close-button:hover {
  color: #374151;
}

.modal-body {
  padding: 24px;
}

/* Draw Number Section */
.draw-number-section {
  margin-bottom: 32px;
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;
}

.form-label {
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.draw-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-select {
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;
}

.form-select:disabled {
  background: #f3f4f6;
  cursor: not-allowed;
}

.custom-draw-toggle {
  display: flex;
  align-items: center;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-text {
  font-size: 14px;
  color: #6b7280;
}

.custom-draw-input {
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
}

.draw-hint {
  font-size: 13px;
  padding: 8px 12px;
  border-radius: 4px;
}

.draw-hint.existing-draw {
  background: #ecfdf5;
  color: #065f46;
}

.draw-hint.new-draw {
  background: #eff6ff;
  color: #1e40af;
}

/* Append Mode Section */
.append-mode-section {
  margin-bottom: 24px;
  padding: 16px;
  background: #fef3c7;
  border-radius: 8px;
  border-left: 4px solid #f59e0b;
}

.append-checkbox {
  margin: 0;
}

.append-checkbox .checkbox-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #78350f;
}

.append-hint {
  font-size: 12px;
  color: #92400e;
  font-weight: normal;
}

/* Upload Buckets */
.upload-buckets-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 24px;
}

@media (max-width: 968px) {
  .upload-buckets-container {
    grid-template-columns: 1fr;
  }
}

.upload-bucket {
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s;
}

.upload-bucket.disabled {
  opacity: 0.6;
  pointer-events: none;
}

.bucket-header {
  padding: 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.bucket-header h4 {
  margin: 0 0 4px 0;
  font-size: 16px;
  color: #1f2937;
}

.bucket-description {
  margin: 0;
  font-size: 13px;
  color: #6b7280;
}

.bucket-drop-zone {
  padding: 32px 16px;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
}

.bucket-drop-zone:not(.disabled):hover {
  background: #f9fafb;
  border-color: #3b82f6;
}

.bucket-drop-zone.drag-over {
  background: #eff6ff;
  border-color: #3b82f6;
}

.bucket-drop-zone.has-file {
  background: #ecfdf5;
  cursor: default;
}

.bucket-drop-zone.disabled {
  cursor: not-allowed;
  background: #f3f4f6;
}

.drop-zone-empty {
  text-align: center;
}

.drop-icon {
  font-size: 48px;
  display: block;
  margin-bottom: 12px;
}

.drop-text {
  margin: 0 0 8px 0;
  color: #374151;
  font-size: 14px;
}

.file-types {
  color: #9ca3af;
  font-size: 12px;
}

.drop-zone-filled {
  width: 100%;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: white;
  border-radius: 8px;
}

.file-icon {
  font-size: 32px;
}

.file-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.file-name {
  font-size: 14px;
  color: #1f2937;
  font-weight: 500;
  word-break: break-word;
}

.file-type-badge,
.type-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.remove-file-btn {
  background: #fee2e2;
  color: #991b1b;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.remove-file-btn:hover {
  background: #fecaca;
}

/* Upload Summary */
.upload-summary {
  padding: 20px;
  background: #eff6ff;
  border-radius: 8px;
  border-left: 4px solid #3b82f6;
}

.upload-summary h5 {
  margin: 0 0 12px 0;
  color: #1e40af;
  font-size: 16px;
}

.summary-list {
  margin: 0 0 12px 0;
  padding-left: 20px;
}

.summary-list li {
  margin-bottom: 8px;
  color: #374151;
  font-size: 14px;
}

/* 🆕 MULTIPLE PDF SUPPORT: Nested file list styling */
.nested-file-list {
  margin: 8px 0 0 20px;
  padding-left: 0;
  list-style: none;
}

.nested-file-list li {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 4px;
}

.summary-note {
  margin: 0;
  font-size: 13px;
  color: #6b7280;
  padding-top: 12px;
  border-top: 1px solid #bfdbfe;
}

/* Modal Footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid #e5e7eb;
}

.btn-cancel,
.btn-upload {
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel {
  background: white;
  border: 1px solid #d1d5db;
  color: #374151;
}

.btn-cancel:hover {
  background: #f9fafb;
}

.btn-upload {
  background: #3b82f6;
  border: none;
  color: white;
}

.btn-upload:hover:not(:disabled) {
  background: #2563eb;
}

.btn-upload:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

/* Spinner Animation */
.spinner-icon {
  display: inline-block;
  margin-right: 8px;
  animation: spin 1s linear infinite;
  font-size: 18px;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>

